import { api } from "./api";
import { workspaces } from "./seed/workspaces";

/**
 * Pure: given the ids a user's own real project memberships cover, and each
 * candidate workspace's own real project ids, pick the workspace containing
 * at least one of the user's own projects. Returns null if none match (e.g.
 * an oversight/funder user with no project-level membership at all, or a
 * user new enough to have no memberships yet) - callers leave the active
 * workspace untouched in that case rather than guessing.
 */
export function resolveHomeWorkspaceId(
  myProjectIds: string[],
  candidates: { workspaceId: string; projectIds: string[] }[],
): string | null {
  const mine = new Set(myProjectIds);
  for (const c of candidates) {
    if (c.projectIds.some((id) => mine.has(id))) return c.workspaceId;
  }
  return null;
}

/**
 * Resolves and returns the workspace id containing the signed-in user's own
 * real project(s), or null if it can't be determined - never throws. This
 * app only maps a small, fixed set of workspaces to real programme ids
 * (seed/workspaces.ts), so checking each one directly is cheap and needs no
 * new backend endpoint.
 *
 * Without this, "active workspace" is just whatever any persona (real or
 * demo) last browsed on this device/browser, with nothing pinning it to the
 * signed-in user's own project - a real reporter/contractor whose account
 * belongs to workspace A silently sees an empty app if the browser was last
 * left on workspace B, with no indication anything is wrong (concretely:
 * this broke a real reporter's raise-ask project list and inbox, both
 * scoped to whichever workspace happened to be active rather than his own).
 *
 * Deliberately not attempted for every login - oversight/funder users are
 * meant to range across workspaces freely (the existing WorkspaceSwitcher
 * already serves them), so this only matters for project-membership-scoped
 * personas (reporter/contractor), and is harmless no-op for the others since
 * they typically have no project-membership rows to match on.
 */
export async function resolveMyHomeWorkspaceId(): Promise<string | null> {
  try {
    const myProjectIds = await api.getMyProjectIds();
    if (myProjectIds.length === 0) return null;

    // allSettled, not all: a single workspace candidate failing (a network
    // blip, a transient 5xx) must never throw away a match another
    // candidate would have found. This was previously Promise.all, which
    // meant one flaky candidate silently collapsed the WHOLE resolution to
    // null - the confirmed root cause of real reporters/contractors
    // landing on the wrong (or default) workspace right after login.
    const settled = await Promise.allSettled(
      workspaces.map(async (w) => {
        // getProgramme is typed as the demo-shaped Programme, but the real
        // API response nests allProjects too (see routers/programme.js) -
        // hooks.ts's useProjects() reaches for the same untyped field the
        // same way, for the same reason.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const programme = (await api.getProgramme(String(w.programmeId))) as any;
        const allProjects = (programme?.allProjects ?? []) as { id: number | string }[];
        return { workspaceId: w.id, projectIds: allProjects.map((p) => String(p.id)) };
      }),
    );
    const candidates = settled
      .filter((r): r is PromiseFulfilledResult<{ workspaceId: string; projectIds: string[] }> => {
        if (r.status === "rejected") {
          console.warn("resolveMyHomeWorkspaceId: a workspace candidate failed to resolve", r.reason);
          return false;
        }
        return true;
      })
      .map((r) => r.value);
    return resolveHomeWorkspaceId(myProjectIds, candidates);
  } catch {
    return null;
  }
}
